import React from "react";
import { MapPin, Phone, Mail, Globe, ExternalLink, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { useQuote } from "./QuoteDialog";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_school-printing-hub/artifacts/lnzv4077_ChatGPT%20Image%20Apr%2026%2C%202026%2C%2003_06_30%20PM.png";

export default function Footer() {
  const { openQuote } = useQuote();

  return (
    <footer
      id="contact"
      data-testid="site-footer"
      className="bg-navy text-white"
    >
      <div className="cmyk-ribbon h-[3px] w-full" aria-hidden />
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 pt-20 pb-10">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 pb-16 border-b border-white/10">
          <div className="lg:col-span-4">
            <div className="bg-white/95 inline-flex rounded-xl p-4 mb-6 shadow-lg">
              <img
                src={LOGO_URL}
                alt="ED Tech Depot — Poster, Banner, Display Solutions for Schools"
                className="h-20 md:h-24 w-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            </div>
            <p className="text-slate-300 max-w-sm leading-relaxed">
              Helping K-12 schools, media centers, and activity departments bring poster
              and banner printing in-house—simply and reliably.
            </p>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.18em] font-bold text-teal mb-5">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-slate-200">
                <MapPin className="w-5 h-5 text-teal flex-shrink-0 mt-0.5" />
                <span data-testid="footer-address">
                  904 Moyer Street
                  <br />
                  Pittsburgh, PA 15204
                </span>
              </li>
              <li className="flex items-center gap-3 text-slate-200">
                <Phone className="w-5 h-5 text-teal flex-shrink-0" />
                <a
                  href="tel:18003965629"
                  data-testid="footer-phone"
                  className="hover:text-teal transition-colors"
                >
                  1-800-396-5629
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-200">
                <Mail className="w-5 h-5 text-teal flex-shrink-0" />
                <a
                  href="mailto:Info@edtech-depot.com"
                  data-testid="footer-email"
                  className="hover:text-teal transition-colors break-all"
                >
                  Info@edtech-depot.com
                </a>
              </li>
              <li className="flex items-center gap-3 text-slate-200">
                <Globe className="w-5 h-5 text-teal flex-shrink-0" />
                <a
                  href="https://www.edtech-depot.com"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="footer-website"
                  className="hover:text-teal transition-colors"
                >
                  www.edtech-depot.com
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.18em] font-bold text-teal mb-5">
              Resources
            </h4>
            <ul className="space-y-3">
              <li>
                <Link
                  data-testid="footer-visibility-check-link"
                  to="/visibility-check"
                  className="inline-flex items-center gap-2 text-slate-200 hover:text-teal transition-colors text-sm"
                >
                  <ClipboardCheck className="w-4 h-4 flex-shrink-0" />
                  Visibility Check<sup>™</sup>
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.18em] font-bold text-teal mb-5">
              Get Started
            </h4>
            <p className="text-slate-300 text-sm mb-5 leading-relaxed">
              Ready to print in-house? Tell us about your school.
            </p>
            <button
              data-testid="footer-request-quote-btn"
              onClick={openQuote}
              title="Opens in a new tab"
              className="group w-full inline-flex items-center justify-center gap-2 bg-teal hover:bg-[#0D6BB1] text-white font-bold px-6 py-3.5 rounded-lg transition-all duration-200 hover:-translate-y-0.5"
            >
              Request a Quote
              <ExternalLink className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </div>
        </div>

        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs text-slate-400">
          <p>
            © {new Date().getFullYear()} ED Tech Depot. All rights reserved.
          </p>
          <p className="font-medium">
            Built for K-12 schools, media centers, and activity departments.
          </p>
        </div>
      </div>
    </footer>
  );
}
